export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export type LogEntry = {
  id: string
  at: number
  level: LogLevel
  scope?: string
  message: string
  data?: unknown
}

type Subscriber = (entry: LogEntry) => void

const MAX_ENTRIES = 400
const STORAGE_KEY = 'DEBUG_LOGS_OVERLAY_ENTRIES_V1'

const subscribers: Set<Subscriber> = new Set()
let entries: LogEntry[] = []
let persistenceLoaded = false
let persistTimer: ReturnType<typeof setTimeout> | undefined

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value)
  } catch {
    return '[unserializable]'
  }
}

const toMessage = (message: unknown) => {
  if (typeof message === 'string') return message
  if (message instanceof Error) return message.message
  return safeStringify(message)
}

const createId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

const loadFromStorageIfNeeded = () => {
  if (persistenceLoaded) return
  persistenceLoaded = true
  if (typeof window === 'undefined') return

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return

    const hydrated: LogEntry[] = parsed
      .filter((e) => e && typeof e === 'object')
      .map((e) => e as Partial<LogEntry>)
      .map((e) => ({
        id: typeof e.id === 'string' ? e.id : createId(),
        at: typeof e.at === 'number' ? e.at : Date.now(),
        level:
          e.level === 'debug' ||
          e.level === 'info' ||
          e.level === 'warn' ||
          e.level === 'error'
            ? e.level
            : 'info',
        scope: typeof e.scope === 'string' ? e.scope : undefined,
        message: typeof e.message === 'string' ? e.message : '[invalid log]',
        data: e.data
      }))

    entries = hydrated.slice(-MAX_ENTRIES)
  } catch {
    // ignore
  }
}

const persistSoon = () => {
  if (typeof window === 'undefined') return
  if (persistTimer) return

  persistTimer = setTimeout(() => {
    persistTimer = undefined
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
    } catch {
      // ignore (quota / private mode / disabled storage)
    }
  }, 250)
}

const addEntry = (partial: Omit<LogEntry, 'id' | 'at'>) => {
  const entry: LogEntry = { id: createId(), at: Date.now(), ...partial }
  entries = [...entries, entry].slice(-MAX_ENTRIES)
  subscribers.forEach((fn) => fn(entry))
  persistSoon()
}

export const logService = {
  getSnapshot: () => {
    loadFromStorageIfNeeded()
    return entries
  },
  subscribe: (fn: Subscriber) => {
    loadFromStorageIfNeeded()
    subscribers.add(fn)
    return () => {
      subscribers.delete(fn)
    }
  },
  clear: () => {
    entries = []
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY)
      } catch {
        // ignore
      }
    }
  },
  push: (level: LogLevel, message: unknown, data?: unknown, scope?: string) => {
    loadFromStorageIfNeeded()
    const messageText = toMessage(message)

    addEntry({
      level,
      scope,
      message: messageText,
      data
    })
  }
}

export const logDebug = (scope: string, message: unknown, data?: unknown) => {
  logService.push('debug', message, data, scope)
}

export const logInfo = (scope: string, message: unknown, data?: unknown) => {
  logService.push('info', message, data, scope)
}

export const logWarn = (scope: string, message: unknown, data?: unknown) => {
  logService.push('warn', message, data, scope)
}

export const logError = (scope: string, message: unknown, data?: unknown) => {
  logService.push('error', message, data, scope)
}
